import React from 'react';
import { darkGreenBarColor, lightGreenBarColor } from '../../theme/xq-custom-theme';
import CustomDivider from '../reusable/CustomDivider';
import HorizontalBarChart from '../reusable/HorizontalBarChart';
import Loader from '../reusable/Loader';
import { calcSelectedTranscriptsDemographicPercentages, createChartConfigs, prepareDemographicData } from './utilityFunctions';
import useFetchData from '../hooks/useFetchData';
import { getDemographicsSummary } from '../../services/dataQcSummaryService';
import useSchools from '../hooks/useSchools';
import { TRANSCRIPTS_CATEGORIES } from '../../constants';
import { useTranscriptSelectionOutletContext } from '../hooks/useTranscriptSelectionOutletContext';

const chartLabelsMap: { [key: string]: string[] } = Object.freeze({
  'American Indian/Alaskan Native': ['American Indian/', 'Alaskan Native'],
  'Black/African American': ['Black/', 'African American'],
  'Hispanic/Latino of any race(s)': ['Hispanic/', 'Latino of any race(s)'],
  'Students with disabilities': ['Students with', 'disabilities'],
  'English language learners': ['English language', 'learner'],
  'Low income students': ['Low income', 'students'],
});

function TranscriptSelectionDrawerContent () {

  const { transcriptSelectionSchool } = useSchools();
  const {selectedTranscriptsSummaryResponse: {selectedTranscriptsSummary}} = useTranscriptSelectionOutletContext();
  
  const { isPending, error, data } = useFetchData({
    fetchData: (eoaId: string, access_token: string) => getDemographicsSummary(eoaId, access_token, transcriptSelectionSchool, false),
    errorMessage: 'Error occurred while fetching transcript selection demographics data!',
    options: {
      dependencies: [transcriptSelectionSchool],
      areDependenciesRequired: true
    },
    initialState: {
      cohort: {
        demographic_percentages: {},
        student_count: 0
      },
      total: {
        demographic_percentages: {},
        student_count: 0
      },
    }
  });

  if (isPending) return <Loader/>;
  if (error) return <p>Demographics data is unavailable!</p>;

  const demographicData = data.total.demographic_percentages;

  const getChartsData = () => {
    if (Object.keys(demographicData).length === 0) return null;

    const preparedData = prepareDemographicData(demographicData);

    return createChartConfigs(preparedData, lightGreenBarColor);
  };
 
  const getSelectionChartData = () => {
    const highFliersCount = selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC].length;
    const onTheCuspCount = selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP].length;
    const strugglingCount = selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.STRUGGLING].length;

    if (!highFliersCount && !onTheCuspCount && !strugglingCount) return null;

    const allSelectedTranscripts = [
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC],
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP],
      ...selectedTranscriptsSummary.selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.STRUGGLING],
    ];

    const selectedTranscriptsDemographicPercentages = calcSelectedTranscriptsDemographicPercentages(data, allSelectedTranscripts);
    
    return createChartConfigs(selectedTranscriptsDemographicPercentages, darkGreenBarColor);
  };

  const chartsData = getChartsData();
  const selectionChartData = getSelectionChartData();

  const formatLables = (labels: string[]) => {
    return labels.map(label => {
      return chartLabelsMap[label] || label;
    });
  };

  return (
    <>
      { !chartsData ? <Loader /> : null }
      { chartsData && chartsData.map(({ title, labels, dataSet }, index) => {
        let dataSets = [dataSet];
        let formattedLabels = formatLables(labels);
        if (selectionChartData) {
          dataSets = dataSets.concat(selectionChartData[index].dataSet);
        } else {
          dataSets = dataSets.concat([{ data: new Array(dataSet.data.length).fill(0), backgroundColor: '' }]);
        }
        return (
          <React.Fragment key={index}>
            { index !== 0 && <CustomDivider orientation="horizontal" />}
            <div className="drawer-section in-transcript-selection">
              <p className="section-title in-transcript-selection">{title}</p>
              <div className="transcript-selection-chart-legend">
                <div className="transcript-selection-chart-legend-label">
                  <div style={{ backgroundColor: 'var(--accent-green-main)' }} className="color-dot"></div>
                  <p>School demographics</p>
                </div>
                <div className="transcript-selection-chart-legend-label">
                  <div style={{ backgroundColor: 'var(--accent-green-dark)' }} className="color-dot"></div>
                  <p>Your selection</p>
                </div>
              </div>
              <div style={{ height: `${formattedLabels.length * 60}px`}}>
                <HorizontalBarChart
                  labels={formattedLabels}
                  dataSets={dataSets}
                  customBarPercentage={1}
                  customCategoryPercentage={0.7}
                  customLayoutPaddingRight={50}
                  dataLabelsFormatter={(value) => `${value}%`}
                  maintainAspectRatio={false}
                  afterFit={(axis) => {
                    axis.width = 137;
                  }}
                  max={100}
                  dataLabelsFontSize={12}
                  ticksFontSize={14}
                />
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}

export default TranscriptSelectionDrawerContent;